import { SummaryItem } from '@interfaces/vendor.model';
import { LeaveTypeEnum } from '@constants/global.enum';

export interface MarkedDate {
  date: string;
  type: string;
  label: string;
}

export class TimesheetCalendarHelper {
  processLeaveDates(data: any): MarkedDate[] {
    const dateTypes = [
      {
        dates: data.paidLeaveDates || [],
        type: 'paid',
        label: 'Paid Leave'
      },
      {
        dates: data.unpaidLeaveDates || [],
        type: 'unpaid',
        label: 'Unpaid Leave'
      },
       {
        dates: data.halfPaidLeaveDates || [],
        type: 'halfpaid',
        label: 'Half Paid Leave'
      },
      {
        dates: data.halfUnpaidLeaveDates || [],
        type: 'halfunpaid',
        label: 'Half Unpaid Leave'
      },
      {
        dates: data.publicHolidayDates || [],
        type: 'public',
        label: 'Public Holiday'
      }
    ];

    const result: MarkedDate[] = [];

    for (const dateType of dateTypes) {
      const isHalfLeave = dateType.type === 'halfpaid' || dateType.type === 'halfunpaid';
      if (isHalfLeave) {
        // Preserve up to 2 occurrences of the same date — dual half-leaves are valid
        const dateCounts = new Map<string, number>();
        for (const date of dateType.dates) {
          const count = (dateCounts.get(date) || 0) + 1;
          if (count <= 2) {
            dateCounts.set(date, count);
            result.push({ date, type: dateType.type, label: dateType.label });
          }
        }
      } else {
        const uniqueDates = [...new Set(dateType.dates)] as string[];
        for (const date of uniqueDates) {
          result.push({ date, type: dateType.type, label: dateType.label });
        }
      }
    }

    return result;
  }

  processLeaveSummary(data: any, SUMMARY_KEYS: any): SummaryItem[] {
    if (data.quarterlyLimit === 0) {
      return [
        { text: SUMMARY_KEYS.UNPAID_LEAVES, value: data.unpaidLeaves || 0 },
        {
          text: SUMMARY_KEYS.QUARTERLY_AVAILED,
          value: data.quarterlyLeavesAvailed || 0,
        },
        { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: data.quarterlyLimit }, // Always include the quarterly limit
      ];
    } else {
      return [
        { text: SUMMARY_KEYS.TOTAL_LEAVES, value: data.totalLeaves || 0 },
        { text: SUMMARY_KEYS.PAID_LEAVES, value: data.paidLeaves || 0 },
        { text: SUMMARY_KEYS.UNPAID_LEAVES, value: data.unpaidLeaves || 0 },
        {
          text: SUMMARY_KEYS.QUARTERLY_AVAILED,
          value: data.quarterlyLeavesAvailed || 0,
        },
        { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: data.quarterlyLimit },
      ];
    }
  }

  getLeaveTypes(
    summary: SummaryItem[],
    SUMMARY_KEYS: any,
  ): { value: string; label: string }[] {
    const quarterlyLimit =
      summary.find((item) => item.text === SUMMARY_KEYS.QUARTERLY_LIMIT)
        ?.value || 0;
    if (quarterlyLimit === 0) {
       return [
        { value: 'halfunpaid', label: 'Half Unpaid Leave' },
        { value: 'unpaid', label: 'Unpaid Leave' }
      ];
    }
    return [
      { value: 'halfpaid', label: 'Half Paid Leave' },
      { value: 'paid', label: 'Paid Leave' },
      { value: 'halfunpaid', label: 'Half Unpaid Leave' },
      { value: 'unpaid', label: 'Unpaid Leave' },
    ];
  }

  dateToString(date: Date): string {
    return (
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0')
    );
  }

  formatDate(dateString: string | number | Date): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  displayMonth(
    dates: { date: string; label: string }[],
    today: { date: string; text: string }[],
    errorHandler: (error: any, message: string) => void,
  ): void {
    setTimeout(() => {
      const cells = document.querySelectorAll('.mat-calendar-body-cell');

      cells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const ariaLabel = htmlCell.getAttribute('aria-label');
        if (!ariaLabel) return;

        try {
          const dateSearch = this.dateToString(new Date(ariaLabel));
          const data = dates.find((f) => f.date === dateSearch);
          const data_today = today.find((f) => f.date === dateSearch);
          if (data) htmlCell.setAttribute('aria-label', data.label);
          if (data_today) htmlCell.setAttribute('aria-label', data_today.text);
        } catch (error) {
          errorHandler(error, 'Failed to fetch leave summary');
        }
      });
    });
  }

  streamOpened(renderer: any, displayMonthCallback: () => void): void {
    setTimeout(() => {
      const buttons = document.querySelectorAll(
        'mat-calendar .mat-icon-button',
      );
      buttons.forEach((btn) =>
        renderer.listen(btn, 'click', () => {
          setTimeout(() => {
            displayMonthCallback();
          });
        }),
      );
      displayMonthCallback();
    });
  }

  getFilteredSummary(summary: SummaryItem[], SUMMARY_KEYS: any): SummaryItem[] {
    const quarterlyLimit = summary.find(
      (item) => item.text === SUMMARY_KEYS.QUARTERLY_LIMIT,
    )?.value;

    if (quarterlyLimit === 0) {
      return summary.filter(
        (item) =>
          item.text !== SUMMARY_KEYS.TOTAL_LEAVES &&
          item.text !== SUMMARY_KEYS.PAID_LEAVES &&
          item.text !== SUMMARY_KEYS.QUARTERLY_LIMIT
      );
    }
    return summary;
  }

  getFilteredLegendItems(legendItems: any[], summary: SummaryItem[], SUMMARY_KEYS: any, LeaveType: any): any[] {
    const quarterlyLimit = summary.find(
      (item) => item.text === SUMMARY_KEYS.QUARTERLY_LIMIT,
    )?.value;

    if (quarterlyLimit === 0) {
      return legendItems.filter(item => item.label !== LeaveType.Paid && item.label !== LeaveType.HalfPaid);
    }
    return legendItems;
  }

  refreshCalendar(apiMarkedDates: MarkedDate[], LeaveTypeEnum: any): void {
    setTimeout(() => {
      const calendarCells = document.querySelectorAll('.mat-calendar-body-cell');
      calendarCells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const ariaLabel = htmlCell.getAttribute('aria-label');
        if (!ariaLabel) return;
        const cellDate = new Date(ariaLabel);
        const dateStr = this.dateToString(cellDate);
        htmlCell.classList.remove(LeaveTypeEnum.Paid, LeaveTypeEnum.Unpaid, LeaveTypeEnum.HalfPaid, LeaveTypeEnum.HalfUnpaid, LeaveTypeEnum.PublicHoliday);
        const apiDate = apiMarkedDates.find(md => md.date === dateStr);
        if (apiDate) {
          if (apiDate.type === LeaveTypeEnum.Paid) htmlCell.classList.add(LeaveTypeEnum.Paid);
          if (apiDate.type === LeaveTypeEnum.Unpaid) htmlCell.classList.add(LeaveTypeEnum.Unpaid);
          if (apiDate.type === LeaveTypeEnum.HalfPaid) htmlCell.classList.add(LeaveTypeEnum.HalfPaid);
          if (apiDate.type === LeaveTypeEnum.HalfUnpaid) htmlCell.classList.add(LeaveTypeEnum.HalfUnpaid);
          if (apiDate.type === LeaveTypeEnum.Public) htmlCell.classList.add(LeaveTypeEnum.PublicHoliday);
        }
      });
    });
  }
}
